import { isAxiosError } from "axios";

const SCHEDULED_ACTION_OVERLAP = "scheduled_action_overlap";

type ProblemDetail = {
  type?: string;
  title?: string;
  errors?: {
    code?: string;
    pointer?: string;
    message?: string;
    meta?: {
      conflicting_action_id?: number;
      conflicting_start_date?: string;
      conflicting_end_date?: string;
    };
  }[];
};

// Scheduled action bounds are calendar dates in a DateTimeField: the picker yields a local date,
// the client posts it as a naive `<date>T00:00:00`, and the server reads that as UTC. The time and
// the zone are padding, so take the date and read it as local midnight rather than as an instant —
// the same `.slice(0, 10)` the ad and budget paths already use.
const asCalendarDate = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00`);

/**
 * The bounds an overlap refusal collided with, keyed on the API's stable
 * `scheduled_action_overlap` code. Undefined for anything else — including an overlap whose
 * payload is missing the bounds, which the UI cannot explain and should report as a plain error.
 */
const getOverlapBounds = (error: unknown) => {
  if (!isAxiosError<ProblemDetail | undefined>(error) || error.response?.status !== 409) {
    return undefined;
  }

  const overlap = error.response.data?.errors?.find((problem) => problem.code === SCHEDULED_ACTION_OVERLAP);
  const { conflicting_start_date: start, conflicting_end_date: end } = overlap?.meta ?? {};

  if (!start || !end) {
    return undefined;
  }

  return { start, end };
};

/**
 * Whether the API refused to overlap an existing scheduled action and told us enough for
 * {@link getScheduledActionConflictRange} to name the collision. Mutations that explain that
 * refusal to the user pass this as `meta.expectedError` so the handled rejection is a breadcrumb
 * rather than a Sentry issue.
 */
export const isScheduledActionOverlap = (error: unknown) => getOverlapBounds(error) !== undefined;

/**
 * Formats the date range an overlap refusal collided with. Undefined for anything the caller
 * should report as a plain error.
 */
export const getScheduledActionConflictRange = (error: unknown, formatter: Intl.DateTimeFormat) => {
  const bounds = getOverlapBounds(error);

  if (!bounds) {
    return undefined;
  }

  return formatter.formatRange(asCalendarDate(bounds.start), asCalendarDate(bounds.end));
};
